import { useMemo } from 'react'
import Styles from './VisualStylePreview.module.scss'
import { TextOverlay } from './TextOverlay'
import { StyleGenerated } from 'screens/VirtualMarketerOnboarding/BrandVisualStyleSelection/GenerationStage'
import { VisualStyleInstagramPreview } from './VisualStyleInstagramPreview'
import { FontPairing } from 'hooks/queries/fontPairings'
import { Font } from 'types'
import { GoogleFontLoader } from 'components/GoogleFontLoader/GoogleFontLoader'
import { BlazeFontLoader } from 'components/BlazeFontLoader/BlazeFontLoader'
import { useFontPreloading } from 'hooks/useFontPreloading'

export interface VisualStylePreviewProps {
  fontPairingId: number | null
  styleGenerated: StyleGenerated
  brandColor?: string
  fontPairings: FontPairing[]
  firstCustomFont: Font
  accountName?: string
  avatarUrl?: string
  brandKitId?: number
  brandVoiceId?: number
  brandKitName?: string
}

export const VisualStylePreview = ({
  fontPairingId,
  brandColor,
  styleGenerated,
  fontPairings,
  firstCustomFont,
  accountName,
  avatarUrl,
  brandKitId,
  brandVoiceId,
  brandKitName,
}: VisualStylePreviewProps) => {
  // Preload all fonts to avoid render delay when switching
  useFontPreloading(fontPairings, firstCustomFont ? [firstCustomFont] : [])

  const selectedFontPairing = useMemo(
    () => fontPairings?.find((fp) => fp.id === fontPairingId),
    [fontPairings, fontPairingId],
  )

  // Build inline styles from header font config or custom font
  const headerFontStyle = useMemo(() => {
    // If user has custom fonts, use the first one
    if (firstCustomFont) {
      return {
        fontFamily: `"${firstCustomFont.font_family}", sans-serif`,
        fontWeight: 400,
        fontStyle: 'normal',
      }
    }

    // Otherwise use the selected font pairing
    const config = selectedFontPairing?.header_font_config
    if (!config) return {}

    return {
      fontFamily: `"${config.family}", sans-serif`,
      fontWeight: config.weight,
      fontStyle: config.italic ? 'italic' : 'normal',
      letterSpacing: `${config.letter_spacing / 1000}em`,
    }
  }, [firstCustomFont, selectedFontPairing?.header_font_config])

  const headerFontFamily = firstCustomFont?.font_family ?? selectedFontPairing?.header_font_config?.family
  const styledUrl = styleGenerated.styleUrl

  return (
    <div className={Styles.container}>
      <GoogleFontLoader family={headerFontFamily} />
      <BlazeFontLoader name={headerFontFamily} />
      <VisualStyleInstagramPreview
        imageUrl={styledUrl}
        accountName={accountName || 'Your Account'}
        avatarUrl={avatarUrl}
        brandKitId={brandKitId}
        brandVoiceId={brandVoiceId}
        brandKitName={brandKitName}
        captionElement={
          headerFontFamily ? (
            <TextOverlay
              key={headerFontFamily}
              fontStyle={headerFontStyle}
              variant="bottom-left"
              textColor={brandColor}
            />
          ) : undefined
        }
      />
    </div>
  )
}
